import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import { DashboardHeader } from '../dashboard';
import {
  SimpleTable,
  Typography,
  Chip,
  Button,
  inventoryCompleteModal,
  TableIconCell,
  TableDots,
  inventoryItemUpdateModal,
  TableCellComment,
  ContainerAvatar,
} from '../../design-system';
import { Filters } from './ViewFilters';
import { useStore } from '../../context';
import {
  smallScreenSize,
  useStatesWithFilterCounter,
  useInventoryOneQuery,
  getSavedSearchParams,
} from '../../hooks';
import {
  InventoryStatusToChipLabels,
  InventoryStatusToChipColors,
  InventoryItemStatusToChipLabels,
  InventoryItemStatusToChipColors,
} from '../../const';
import { i18n } from '../../i18n';
import {
  formatContainerPlace,
  formatContainerType,
  momentTextFormat,
  showToRoles,
} from '../../utils';
import { ContainerType, InventoryGetOneQuery } from '../../types';

interface InventoryViewPageProps {
  innerScroll?: boolean;
}

export const InventoryViewPage = styled.div<InventoryViewPageProps>`
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: max-content;
  grid-row-gap: 3rem;

  height: max-content;
  padding-bottom: var(--spacing-10);

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}

  @media (max-width: ${smallScreenSize}px) {
    padding-bottom: var(--spacing-3);
  }
`;

const TableCellLocation = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  justify-content: space-between;

  width: 100% !important;
`;

export const InventoryViewHeader = ({
  yardName,
  yardId,
  clientData,
  handleCheckWithClientReportClick,
  handleCancelClick,
}: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const { id } = useParams();
  const navigate = useNavigate();

  const handleCompleteClick = () => {
    inventoryCompleteModal.id = id;
    inventoryCompleteModal.yardName = yardName;
    inventoryCompleteModal.yardId = yardId;
    inventoryCompleteModal.submitClick = () => {
      navigate('/dashboard/inventory');
    };
    inventoryCompleteModal.open();
  };

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {yardName}
      </Typography>
      {!!clientData && !showToRoles(['YARD_COMPANY_VIEWER'], user) && (
        <Button size={'medium'} onClick={handleCancelClick} data-color={'white'}>
          {i18n.t('button.cancel')}
        </Button>
      )}
      {!clientData && !showToRoles(['YARD_COMPANY_VIEWER'], user) && (
        <Button size={'medium'} onClick={handleCheckWithClientReportClick}>
          {i18n.t('button.checkWithClientReport')}
        </Button>
      )}
      {!showToRoles(['YARD_COMPANY_VIEWER'], user) && (
        <Button size={'medium'} onClick={handleCompleteClick}>
          {i18n.t('button.finish')}
        </Button>
      )}
    </DashboardHeader>
  );
};

export const InventoryViewBody = ({ setData, clientData, handleClickImage }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const { id } = useParams();

  const [openDotsMenuIndex, setDotsMenuIndex] = useState(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    clientId: '',
    number: '',
    status: 'All',
  });

  const filteredClientData = useMemo(() => {
    const data = clientData ? { ...clientData } : null;
    if (!data) return null;

    if (filteredStates.status === 'DISMATCHED') {
      data.items = data.items.filter(
        (item: any) =>
          item.clientStatus === 'NOT_FOUND' ||
          item.status === 'NOT_FOUND' ||
          item.status === 'EXTRA_FOUND',
      );
    } else if (filteredStates.status === 'FOUND') {
      data.items = data.items.filter(
        (item: any) => item.clientStatus === 'FOUND' && item.status === 'FOUND',
      );
    }

    return data;
  }, [JSON.stringify(clientData), JSON.stringify(filteredStates)]);
  const { data, isFetching, refetch } = useInventoryOneQuery({
    ...(clientData ? {} : { ...filteredStates }),
    id,
  });

  const filteredInventoryItems = useMemo(() => {
    const filteredItems =
      (filteredClientData?.items as InventoryGetOneQuery['data']['items']) ||
      data?.data.items ||
      [];

    if (filteredStates.status === 'DISMATCHED') {
      return filteredItems.filter(
        (item) =>
          item.status === 'NOT_FOUND' ||
          item.unchecked?.block !== item.checked?.block ||
          item.unchecked?.row !== item.checked?.row ||
          item.unchecked?.slot !== item.checked?.slot,
      );
    }

    return filteredItems;
  }, [JSON.stringify(filteredClientData?.items), JSON.stringify(data?.data.items), states.status]);

  useEffect(() => {
    if (isFetching) return;

    setData(data?.data);
  }, [isFetching]);

  useEffect(() => {
    if (clientData) return;

    setStates('cancelall', getSavedSearchParams(states));
  }, [JSON.stringify(clientData)]);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    setStates(...args);
  };

  const handleDotsOptionSelect = (item: any, val: any) => {
    if (val === 'edit') {
      inventoryItemUpdateModal.yardId = data?.data.yard.id;
      inventoryItemUpdateModal.id = item.id;
      inventoryItemUpdateModal.comment = item.comment;
      inventoryItemUpdateModal.number = item.number;
      inventoryItemUpdateModal.status = item.status;

      inventoryItemUpdateModal.submitClick = () => {
        refetch();
      };
      inventoryItemUpdateModal.open();
    }
  };

  return (
    <>
      <SimpleTable
        data={{ data: data?.data.assignedTo ?? [], totalCount: data?.data.assignedTo?.length ?? 0 }}
        filters={() => (
          <Filters
            setStates={handleFilterChange}
            {...states}
            yardId={data?.data.yard.id ?? ''}
            clientData={clientData}
          />
        )}
        tableRowProps={(inventory, i) => ({
          key: inventory.firstName + ' ' + inventory.lastName + i,
          onClick: () => {},
        })}
        columns={[
          {
            headText: () => i18n.t('label.blocks'),
            body: (inventory) => (
              <TableCell component={'th'} align={'left'}>
                {inventory.blocks.map((block: any) => block.block).join(', ')}
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.assignedTo'),
            bodyText: (inventory) => inventory.firstName + ' ' + inventory.lastName,
          },
          {
            headText: () => i18n.t('label.finished'),
            bodyText: (inventory) => inventory.completedCount,
          },
          {
            headText: () => i18n.t('label.status'),
            body: (inventory) => (
              <TableCell component={'th'} align={'left'}>
                <Chip
                  size={'small'}
                  label={InventoryStatusToChipLabels[inventory.status]}
                  color={InventoryStatusToChipColors[inventory.status]}
                />
              </TableCell>
            ),
          },
        ]}
      />
      <SimpleTable
        data={{
          data: filteredInventoryItems,
          totalCount: filteredInventoryItems.length,
        }}
        tableRowProps={(item, i) => ({
          key: item.number + i,
          onClick: () => {},
        })}
        columns={[
          {
            column: 1,
            isHidden: () => showToRoles(['YARD_COMPANY_VIEWER'], user),
            headText: () => i18n.t('label.actions'),
            body: (item, i) => (
              <TableIconCell component={'th'} align={'left'}>
                <TableDots
                  open={openDotsMenuIndex === i}
                  onToggle={() => handleDotsToggle(i)}
                  options={[{ label: i18n.t('dropdown.edit'), value: 'edit' }]}
                  onSelect={(val: any) => {
                    handleDotsOptionSelect(item, val);
                  }}
                />
              </TableIconCell>
            ),
          },
          {
            headText: () => i18n.t('label.photo'),
            body: (item) => (
              <TableIconCell component={'th'} align={'left'}>
                <ContainerAvatar
                  images={item.checked?.image || []}
                  onClick={() => handleClickImage(item.checked?.image || [])}
                />
              </TableIconCell>
            ),
          },
          {
            headText: () => i18n.t('label.client'),
            body: (item) => (
              <TableCell component={'th'} align={'left'}>
                {item.clientYard?.client.code}
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.containerNumber'),
            bodyText: (item) => item.number,
          },
          {
            headText: () => i18n.t('label.type'),
            bodyText: (item) =>
              item.checked?.type || item.unchecked?.type
                ? formatContainerType(item.checked?.type || (item.unchecked?.type as ContainerType))
                : '',
          },
          {
            headText: () => i18n.t('label.expectedLocation'),
            body: (item) => (
              <TableCell align={'left'}>
                <TableCellLocation>
                  <span>
                    {formatContainerPlace(item.unchecked)
                      ? formatContainerPlace(item.unchecked)
                      : 'N / A'}
                  </span>
                  <span>
                    {formatContainerPlace(item.checked)
                      ? formatContainerPlace(item.checked)
                      : 'N / A'}
                  </span>
                </TableCellLocation>
              </TableCell>
            ),
            column: 15,
          },
          {
            headText: () => i18n.t('label.client'),
            body: ({ clientStatus }) => (
              <TableCell align={'left'}>
                <Chip
                  size={'small'}
                  label={InventoryItemStatusToChipLabels[clientStatus]}
                  color={InventoryItemStatusToChipColors[clientStatus]}
                />
              </TableCell>
            ),
            column: 1,
            isHidden: () => !clientData,
          },
          {
            headText: () => (clientData ? i18n.t('common.yard') : i18n.t('label.status')),
            body: ({ status }) => (
              <TableCell align={'left'}>
                <Chip
                  size={'small'}
                  label={InventoryItemStatusToChipLabels[status]}
                  color={InventoryItemStatusToChipColors[status]}
                />
              </TableCell>
            ),
          },
          {
            headText: () => i18n.t('label.comment'),
            body: (item) => <TableCellComment comment={item.comment} />,
            column: 15,
          },
          {
            headText: () => i18n.t('label.scannedTime'),
            bodyText: (item) => (item.updatedAt ? momentTextFormat(item.updatedAt, user) : ''),
          },
        ]}
      />
    </>
  );
};
